import React, { useState } from 'react';
import { Button } from '../Button';
import { Badge } from '../Badge';
import { Separator } from '../Separator';
type BillingCycle = 'monthly' | 'annual';
function BillingToggle({
  value,
  onChange
}: {
  value: BillingCycle;
  onChange: (v: BillingCycle) => void;
}) {
  const base = 'flex-1 px-5 py-2 text-sm font-medium rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 focus-visible:ring-offset-2';
  const active = 'bg-navy-500 text-white';
  const inactive = 'text-gray-600 hover:text-gray-900';
  return <div role="tablist" aria-label="Billing cycle" className="inline-flex items-center gap-1 p-1 border border-gray-300 bg-gray-50 rounded-full" data-id="element-212">
      <button type="button" role="tab" aria-selected={value === 'monthly'} onClick={() => onChange('monthly')} className={`${base} ${value === 'monthly' ? active : inactive}`} data-id="element-213">
        Monthly
      </button>
      <button type="button" role="tab" aria-selected={value === 'annual'} onClick={() => onChange('annual')} className={`${base} inline-flex items-center justify-center gap-2 ${value === 'annual' ? active : inactive}`} data-id="element-214">
        <span data-id="element-215">Annual</span>
        <span className={`text-[10px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded-full border ${value === 'annual' ? 'border-white/40 text-white' : 'border-gray-400 text-gray-600'}`} data-id="element-216">
          −17%
        </span>
      </button>
    </div>;
}
function BulletList({
  items,
  mark,
  tone = 'light'
}: {
  items: string[];
  mark: '×' | '✓';
  tone?: 'light' | 'dark';
}) {
  const isDark = tone === 'dark';
  return <ul className="flex flex-col gap-3" data-id="element-217">
      {items.map(item => <li key={item} className={`flex items-start gap-3 text-sm leading-relaxed ${isDark ? 'text-gray-100' : 'text-gray-800'}`} data-id="element-218">
          <span className={`mt-0.5 inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full text-xs leading-none ${isDark ? 'bg-orange-brand-500 text-white' : 'border border-gray-400 text-gray-700'}`} aria-hidden data-id="element-219">
            {mark}
          </span>
          <span data-id="element-220">{item}</span>
        </li>)}
    </ul>;
}
const agencyCons = ['$6,000–$10,000+/month', '$200–$600 per meeting booked', 'Can be rude to your customers', "Don't know your playbook", 'High turnover — constant retraining'];
const speedPros = ['Fraction of the cost', 'No per-meeting fees', 'Available 24/7 — never misses a lead', 'Always polite and professional', 'Knows your playbook perfectly', 'No staff to hire or train'];
function PricingCard({
  cycle
}: {
  cycle: BillingCycle;
}) {
  const price = cycle === 'monthly' ? '$240' : '$200';
  const suffix = cycle === 'monthly' ? '/mo' : '/mo, billed annually';
  return <div className="border-2 border-navy-500 rounded-2xl bg-white p-6 sm:p-8 w-full max-w-md flex flex-col gap-6" data-id="element-221">
      <div data-id="element-222">
        <div className="flex items-baseline gap-1" data-id="element-223">
          <span className="text-4xl sm:text-5xl font-bold text-navy-500 leading-none" data-id="element-224">
            {price}
          </span>
          <span className="text-base text-gray-500" data-id="element-225">{suffix}</span>
        </div>
        <p className="text-sm text-gray-600 mt-3 leading-relaxed" data-id="element-226">
          24/7 lead response · Unlimited meetings · Always on-script
        </p>
      </div>

      <Separator data-id="element-227" />

      <div data-id="element-228">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-229">
          {cycle === 'annual' ? <>
              <span className="text-sm font-semibold text-gray-900" data-id="element-230">
                AI training fee
              </span>
              <span className="text-sm text-gray-400 line-through" data-id="element-231">$500</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-232">FREE</span>
              <span className="inline-flex items-center text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-0.5 rounded-full bg-gray-900 text-white" data-id="element-233">
                Included with annual
              </span>
            </> : <span className="text-sm font-semibold text-gray-900" data-id="element-234">
              + $500 one-time AI training fee
            </span>}
        </div>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed text-left" data-id="element-235">
          We learn your playbook, qualifying questions, and voice so the AI
          sounds like your best rep.
          {cycle === 'monthly' && <>
              {' '}
              <span className="text-gray-900 font-medium" data-id="element-236">
                Waived when you choose annual.
              </span>
            </>}
        </p>
      </div>

      <Button variant="primary" size="large" className="w-full !bg-navy-500 !border-navy-500 hover:!bg-navy-800 hover:!border-navy-800" rightIcon={<span aria-hidden data-id="element-238">→</span>} data-id="element-237">
        Start Converting Leads
      </Button>

      <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-239">
        ✦ 30-day money-back guarantee · No contracts · Cancel anytime
      </p>
    </div>;
}
export function Pricing() {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  return <section id="pricing" className="w-full border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-24 scroll-mt-20" data-id="element-240">
      <div className="mx-auto max-w-[1280px]" data-id="element-241">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center max-w-3xl mx-auto mb-10 sm:mb-14" data-id="element-242">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-navy-500 text-white uppercase tracking-[0.15em] text-[11px] font-semibold" data-id="element-243">
            The Real Cost
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight leading-[1.1]" data-id="element-244">
            Save thousands compared to a traditional agency.
          </h2>
          <p className="text-base text-gray-600 leading-relaxed" data-id="element-245">
            Traditional agencies charge $6,000–$10,000/month plus per-meeting
            fees — and still deliver reps who are rude and off-script.
          </p>
        </div>

        {/* Comparison cards — matched border weights for pixel-perfect alignment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10 sm:mb-20" data-id="element-246">
          <div className="border-2 border-gray-200 rounded-2xl bg-white p-6 sm:p-8 flex flex-col gap-6" data-id="element-247">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-248">
              Traditional Agency
            </h3>
            <BulletList items={agencyCons} mark="×" data-id="element-249" />
          </div>

          <div className="border-2 border-navy-500 rounded-2xl bg-navy-500 text-white p-6 sm:p-8 flex flex-col gap-6 relative" data-id="element-250">
            <div className="absolute -top-3 left-8" data-id="element-251">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-brand-500 text-white uppercase tracking-[0.15em] text-[10px] font-semibold" data-id="element-252">
                Recommended
              </span>
            </div>
            <h3 className="text-lg font-semibold text-white" data-id="element-253">
              Speed to Lead AI
            </h3>
            <BulletList items={speedPros} mark="✓" tone="dark" data-id="element-254" />
          </div>
        </div>

        {/* Pricing card section */}
        <div className="flex flex-col items-center gap-6 text-center" data-id="element-255">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 tracking-tight leading-tight" data-id="element-256">
            Simple, transparent pricing.
          </h3>
          <p className="text-base text-gray-600" data-id="element-257">
            No hidden fees. No per-meeting charges. No surprises.
          </p>

          <BillingToggle value={cycle} onChange={setCycle} data-id="element-258" />

          <PricingCard cycle={cycle} data-id="element-259" />
        </div>
      </div>
    </section>;
}